import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWindowSize } from 'react-use';

import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';
import { Alert, AlertTitle } from '@material-ui/lab';

import { useMessageDialog } from '../../../../../../common/hooks/useMessageDialog/useMessageDialog';
import GenericObject from '../../../../../../typesAdditional/GenericObject';
import LoadingSimulationAlert from '../../components/LoadingSimulationAlert/LoadingSimulationAlert';
import SimulationRecapAlert from '../../components/SimulationRecapAlert/SimulationRecapAlert';
import StepFooter from '../../components/StepFooter/StepFooter';
import useSimulateRegistration from '../../hooks/useSimulateRegistration/useSimulateRegistration';
import { GenericStepProps } from '../types';
import CardSessionsSelector from './components/CardsSessionsSelector/CardSessionsSelector';
import DiscountSelector from './components/DiscountSelector/DiscountSelector';
import Header from './components/Header/Header';
import SuggestionsDialog from './components/SuggestionsDialog/SuggestionsDialog';
import TableSessionsSelector from './components/TableSessionsSelector/TableSessionsSelector';
import useHandleSelect from './hooks/useHandleSelect';
import useInternalNextStep from './hooks/useInternalNextStep';
import useTownFromParams from './hooks/useTownFromParams';
import CustomTextField from '../../../../../../common/components/CustomTextField/CustomTextField';
import SpecialDietsV2Selector from './components/SpecialDietsV2Selector/SpecialDietsV2Selector';
import usePriceOffset from './hooks/usePriceOffset';

interface Props extends GenericStepProps {
    courseData: any;
    setSelectedLocationId: (locationId: string) => void;
    setSelectedSessions: (sessions: any) => void;
    setSuggestedSessions: (sessions: any) => void;
    initialSelectedLocationId: string;
    initialSelectedSessions: any[];
    questionResponse: string;
    setQuestionResponse: (res: string) => void;
}

const RegistrationStep6 = (props: Props) => {
    const { courseId, courseData, data, prevStep, nextStep, onChange, rawOnChange, gtmStepPush, setSelectedLocationId: externalSetSelectedLocationId, setSelectedSessions: externalSetSelectedSessions, setSuggestedSessions: externalSetSelectedSuggestedSessions, initialSelectedSessions, initialSelectedLocationId, preselectedLocation, questionResponse, setQuestionResponse, welfareData } = props;
    const { locationType, locations, sessionType, enableSpecialDiets, enableSpecialDietsV2 } = courseData;

    const [selectedLocationId, setSelectedLocationId] = useState(initialSelectedLocationId);

    const [isLoading, setIsLoading] = useState(false);

    const { canUserChangeLocation } = useTownFromParams({ locations, setSelectedLocationId });

    const residenceTownLegalId = data?.participant?.address?.town_legal_id ?? '';
    const schoolGrade = data?.participant?.schoolGrade ?? 0;

    const [showMessageDialog, closeMessageDialog] = useMessageDialog();

    const [isSuggestionsDialogOpen, setIsSuggestionsDialogOpen] = useState(false);
    const [selectedSessionsSuggestions, setSelectedSessionsSuggestions] = useState<GenericObject[]>([]);

    useEffect(() => {
        const campTown = (courseData?.locations?.find((l: any) => l.id === selectedLocationId) ?? preselectedLocation)?.town?.name;

        const analyticsData = {
            visitorLoggedIn: '1',
            summer_camp: courseData?.name ?? '',
            camp_town: campTown ?? '',
            student_country: 'Italy',
            student_city: data?.participant?.address?.town_name ?? '',
            student_zip: data?.participant?.address?.zip ?? '',
        };

        gtmStepPush(4, analyticsData);
    }, [gtmStepPush, courseData, selectedLocationId, data, preselectedLocation]);

    const {
        simulateRegistrationData,
        selectedSessions,
        setSelectedSessions,
        hasFoodService,
        getSessionsLabel,
        countSessions,
        countSessionsFoodService,
        countQueueSessions,
        isLoading: isSimulationLoading,
        isSelected,
        isFoodSelected,
        toPay,
        saving,
        additionalSaving
    } = useSimulateRegistration({ courseId, courseData, data, sessionType, selectedLocationId, setSelectedLocationId, initialSelectedSessions, welfareData });

    const shouldShowSpecialDietsForm = (enableSpecialDiets || enableSpecialDietsV2) && (!hasFoodService || selectedSessions.some(s => s.foodService));

    useEffect(() => {
        if (!shouldShowSpecialDietsForm) {
            rawOnChange('special_diets', '');   // legacy special diets
            rawOnChange('specialDietsV2', []);
        }
    }, [rawOnChange, shouldShowSpecialDietsForm])

    const { width } = useWindowSize();

    const selectedLocation = locations.find((l: any) => l.id === selectedLocationId);

    useEffect(() => {
        if (locationType !== 'multi_location') {
            setSelectedLocationId(locations[0]?.id ?? '');
        }
    }, [locationType, locations]);

    const { handleSelect, handleFoodSelect } = useHandleSelect({ selectedSessions, setSelectedSessions });
    const { handleSelect: handleSelectSuggestions, handleFoodSelect: handleFoodSelectSuggestions } = useHandleSelect({ selectedSessions: selectedSessionsSuggestions, setSelectedSessions: setSelectedSessionsSuggestions });

    const isSuggestionSelected = (sessionId: string) => {
        return !!selectedSessionsSuggestions.find(s => s.id === sessionId);
    };

    const internalPrevStep = useCallback(() => {
        externalSetSelectedLocationId(selectedLocationId);
        externalSetSelectedSessions(selectedSessions);

        if (prevStep) {
            prevStep();
        }
    }, [selectedLocationId, selectedSessions, externalSetSelectedLocationId, externalSetSelectedSessions, prevStep]);

    const internalNextStep = useInternalNextStep({
        courseId,
        selectedLocationId,
        selectedSessions,
        data,
        sessionType,
        countSessions,
        countQueueSessions,
        setIsLoading,
        externalSetSelectedLocationId,
        externalSetSelectedSessions,
        externalSetSelectedSuggestedSessions,
        nextStep,
        selectedSessionsSuggestions,
        allowEmptyRegistration: courseData.allowEmptyRegistration,
        simulateRegistrationData,
        welfareData
    });

    const realSessions = simulateRegistrationData?.discounts?.selected?.customPricing?.sessions ?? simulateRegistrationData?.sessions ?? [];

    const pricesNotAvailable = realSessions?.some((session: any) => {
        return session?.prices?.course?.actual?.price <= 0;
    });

    const isLoadingReal = isLoading || isSimulationLoading;

    const nextStepCheckSuggestions = useCallback(() => {
        const cardinali = ['ulteriore', 'secondo', 'terzo', 'quarto'];
        const nCardinale = cardinali[selectedSessions.length];

        if ((simulateRegistrationData?.suggestions ?? []).length > 0 && selectedSessions.length > 0) {
            showMessageDialog({
                title: 'Sconto dedicato disponibile',
                message: (
                    <>
                        {(courseData.category.name.toLowerCase().includes('adult') ? 'Vuoi aggiungere un ulteriore corso' : 'Vuoi aggiungere un ' + nCardinale + ' giorno su un Comune diverso') + ' ed ottenere una scontistica dedicata?'}
                    </>
                ),
                actions: [
                    {
                        text: 'No',
                        action: () => {
                            closeMessageDialog();
                            internalNextStep();
                        }
                    },
                    {
                        text: 'Sì',
                        action: () => {
                            closeMessageDialog();
                            setIsSuggestionsDialogOpen(true);
                        }
                    }
                ]
            });
        } else {
            internalNextStep();
        }
    }, [selectedSessions, closeMessageDialog, courseData, internalNextStep, showMessageDialog, simulateRegistrationData]);

    const resetSessions = useMemo(() => () => setSelectedSessionsSuggestions([]), []);

    const questions = simulateRegistrationData.sessions.filter((s: any) => s.question).map((s: any) => s.question);
    const selectedAnswer = (questions.length > 0 && questionResponse === questions[0]) ? 'yes' : 'no';

    const handleChangeQuestion = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = (event.target as HTMLInputElement).value;
        if (value === 'yes') {
            setQuestionResponse(questions.length > 0 ? questions[0] : '');
        } else {
            setQuestionResponse('');
        }
    };

    const { getSessionPriceWithOffset } = usePriceOffset({
        suggestions: simulateRegistrationData?.suggestions ?? [],
        savedSessions: simulateRegistrationData?.suggestionsPriceGroups ?? [],
        currentRegistrationSessions: selectedSessionsSuggestions
    });

    useEffect(() => {
        const sessionsToRemove = simulateRegistrationData.sessions.filter((s: any) => !((!s.question || s.question === questionResponse) && questionResponse !== s.disabledQuestion));
        sessionsToRemove.forEach((s: any) => {
            if (isSelected(s.id)) {
                handleSelect(s.id, false);
            }
        });
    }, [simulateRegistrationData, isSelected, handleSelect, questionResponse]);

    const singleSessionEvent = (sessionType === 'single_event' && ((simulateRegistrationData?.discounts?.selected?.customPricing?.sessions ?? simulateRegistrationData.sessions ?? []).length === 1));

    return (
        <>
            <Header
                locations={locations}
                locationType={locationType}
                selectedLocationId={selectedLocationId}
                setSelectedLocationId={setSelectedLocationId}
                selectedLocation={selectedLocation}
                setSelectedSessions={setSelectedSessions}
                canUserChangeLocation={canUserChangeLocation}
            />

            {selectedLocationId && (
                <>
                    <DiscountSelector
                        discounts={simulateRegistrationData?.discounts}
                        data={data}
                        rawOnChange={rawOnChange}
                    />

                    {questions.length === 1 && (
                        <div style={{ marginBottom: '20px' }}>
                            <Typography variant='h4' style={{ fontSize: '1.4em', textAlign: 'center', margin: '8px', marginBottom: '10px' }}>
                                {questions[0]}
                            </Typography>

                            <FormControl component='fieldset'>
                                <RadioGroup aria-label='gender' name='question' value={selectedAnswer} onChange={handleChangeQuestion}>
                                    <FormControlLabel value='no' control={<Radio color='primary' />} label='No' />
                                    <FormControlLabel value='yes' control={<Radio color='primary' />} label='Sì' />
                                </RadioGroup>
                            </FormControl>
                        </div>
                    )}

                    {questions.length > 1 && (
                        <div style={{ marginBottom: '20px' }}>
                            <FormControl component='fieldset'>
                                <RadioGroup aria-label='gender' name='question' value={questionResponse} onChange={(e) => setQuestionResponse((e.target as HTMLInputElement).value)}>
                                    {questions.map((q: string) => {
                                        return (
                                            <FormControlLabel value={q} control={<Radio color='primary' />} label={q} />
                                        )
                                    })}
                                </RadioGroup>
                            </FormControl>
                        </div>
                    )}

                    {pricesNotAvailable && (
                        <Alert severity='info' style={{ paddingBottom: '0px', marginBottom: '24px' }}>
                            <AlertTitle>Quote in fase di definizione</AlertTitle>
                        </Alert>
                    )}

                    {selectedLocationId === '96572ecf-05d5-4171-9f44-5c5e92951a09' && (
                        <Alert severity='info' style={{ paddingBottom: '0px', marginBottom: '14px' }}>
                            <AlertTitle>Settimane 20-24 Giugno e 27 Giugno - 1 Luglio</AlertTitle>
                            <p style={{ marginTop: 0 }}>
                                Iscrizioni aperte solo a PRIMARIA E SECONDARIA
                            </p>
                            <AlertTitle>Settimane 4-8 e 11-15 luglio</AlertTitle>
                            <p style={{ marginTop: 0 }}>
                                Iscrizioni aperte solo all'INFANZIA
                            </p>
                        </Alert>
                    )}

                    {['f84361c3-6d02-4bcc-acc1-f35951719aed', '11848520-a8eb-496d-9cc7-ec64bf9d1f19', '9f606eee-2416-4018-bc27-89ccc10be8e5'].includes(selectedLocationId) && residenceTownLegalId === 'H176' && (
                        <Alert severity='info' style={{ paddingBottom: '0px', marginBottom: '14px' }}>
                            <AlertTitle>PER I RESIDENTI DI RANICA IL COMUNE ELARGIRÀ CONTRIBUTI DEDICATI</AlertTitle>
                            <p style={{ marginTop: 0 }}>
                                Le procedure di richiesta contributo residenti sono da presentare direttamente presso gli uffici comunali.
                            </p>
                        </Alert>
                    )}

                    {(selectedLocationId === '1db4d6c2-9cba-473c-baf8-900f8f684644' || selectedLocationId === '5051c61b-2640-4b1a-8af4-b726a50560f1') && (
                        <Alert severity='info' style={{ paddingBottom: '0px', marginBottom: '14px' }}>
                            <p style={{ marginTop: 0 }}>
                                Le iscrizioni sono aperte solo agli alunni della scuola "Sacro Cuore".
                            </p>
                        </Alert>
                    )}

                    {(selectedLocationId === '768a093a-8d28-4803-a4fb-cd4745e98151' && !questionResponse) ? (
                        <Alert severity='warning' style={{ paddingBottom: '0px', marginBottom: '14px' }}>
                            <p style={{ marginTop: 0 }}>
                                Siamo spiacenti ma il servizio di Posticipo Sportivo è aperto solo agli iscritti della scuola dell'infanzia Papa Giovanni XXIII. Il nostro corso PPS è aperto a tutti in tanti altri comuni della bergamasca.
                            </p>
                        </Alert>
                    ) : (
                        <>
                            <>
                                {!singleSessionEvent && (
                                    <Typography variant='h4' style={{ fontSize: '1.4em', textAlign: 'center', margin: '8px', marginBottom: '10px' }}>
                                        Seleziona {(sessionType === 'single_event' || sessionType === 'weekly_events') ? 'i giorni a cui' : 'le settimane a cui'} desideri iscriverti
                                    </Typography>
                                )}

                                <div style={{ marginBottom: '16px' }}>
                                    {width > 700 ? (
                                        <TableSessionsSelector
                                            sessionType={sessionType}
                                            hasFoodService={hasFoodService}
                                            simulateRegistrationData={simulateRegistrationData}
                                            isSelected={isSelected}
                                            isFoodSelected={isFoodSelected}
                                            handleSelect={handleSelect}
                                            handleFoodSelect={handleFoodSelect}
                                            questionResponse={questionResponse}
                                            schoolGrade={schoolGrade}
                                            pricesNotAvailable={pricesNotAvailable}
                                            singleSessionEvent={singleSessionEvent}
                                            isSummer={(courseData?.season?.name ?? '').toLowerCase().includes('estiva')}
                                        />
                                    ) : (
                                        <CardSessionsSelector
                                            sessionType={sessionType}
                                            hasFoodService={hasFoodService}
                                            simulateRegistrationData={simulateRegistrationData}
                                            isSelected={isSelected}
                                            isFoodSelected={isFoodSelected}
                                            handleSelect={handleSelect}
                                            handleFoodSelect={handleFoodSelect}
                                            questionResponse={questionResponse}
                                            schoolGrade={schoolGrade}
                                            pricesNotAvailable={pricesNotAvailable}
                                            singleSessionEvent={singleSessionEvent}
                                            isSummer={(courseData?.season?.name ?? '').toLowerCase().includes('estiva')}
                                        />
                                    )}
                                </div>
                            </>
                        </>
                    )}
                </>
            )
            }

            {
                enableSpecialDiets && shouldShowSpecialDietsForm && (
                    <CustomTextField
                        value={data.special_diets === undefined ? simulateRegistrationData?.savedRegistration?.specialDiets : undefined}
                        label='Intolleranze o diete speciali'
                        path={'special_diets'}
                        data={data}
                        dataOnChange={onChange}
                        variant='outlined'
                        minRows={3}
                        multiline
                        style={{ marginTop: '6px' }}
                        disabled={Object.keys(simulateRegistrationData).length === 1}
                    />
                )
            }

            {
                enableSpecialDietsV2 && shouldShowSpecialDietsForm && (
                    <SpecialDietsV2Selector
                        selectedSpecialDiets={(data.specialDietsV2 === undefined ? simulateRegistrationData?.savedRegistration?.specialDietsV2 : data.specialDietsV2) ?? []}
                        rawOnChange={rawOnChange}
                        disabled={Object.keys(simulateRegistrationData).length === 1}
                    />
                )
            }

            {
                selectedLocationId === '7d71e6f5-dcda-4319-92b3-43246c15a55f' && (
                    <Alert severity='info' style={{ paddingBottom: '0px', marginBottom: '14px' }}>
                        <p style={{ marginTop: 0 }}>
                            N.B. : la quota settimanale dei pasti, 30€ - 5 pasti, sarà addebitata con Rid dall'Opera Sant'Alessandro.
                        </p>
                    </Alert>
                )
            }

            <div style={{ marginTop: '16px' }}>
                {isLoadingReal ? (
                    <LoadingSimulationAlert />
                ) : (
                    <>
                        {!selectedLocationId && (
                            <Alert severity='warning' style={{ paddingBottom: '0px' }}>
                                <AlertTitle>Seleziona paese</AlertTitle>
                                <p style={{ marginTop: '0px' }}>
                                    Seleziona il paese dove desideri iscriverti.
                                </p>
                            </Alert>
                        )}

                        {(selectedLocationId && !countSessions && !countQueueSessions && !(selectedLocationId === '768a093a-8d28-4803-a4fb-cd4745e98151' && !questionResponse) && !(singleSessionEvent && courseData.allowEmptyRegistration)) && (
                            <Alert severity='warning' style={{ paddingBottom: '0px' }}>
                                <AlertTitle>Seleziona {(sessionType === 'single_event' || sessionType === 'weekly_events') ? 'i giorni a cui' : 'le settimane a cui'} desideri iscriverti.</AlertTitle>
                                <p style={{ marginTop: '0px' }}>
                                    È obbligatorio selezionare almeno {(sessionType === 'single_event' || sessionType === 'weekly_events') ? 'un giorno' : 'una settimana'}.
                                </p>
                            </Alert>
                        )}

                        {(!!((countSessions + countQueueSessions) > 0 || (singleSessionEvent && courseData.allowEmptyRegistration))) && (
                            <SimulationRecapAlert
                                simulateRegistrationData={simulateRegistrationData}
                                countSessions={countSessions}
                                countSessionsFoodService={countSessionsFoodService}
                                countQueueSessions={countQueueSessions}
                                toPay={toPay}
                                saving={saving}
                                additionalSaving={additionalSaving}
                                getSessionsLabel={getSessionsLabel}
                                pricesNotAvailable={pricesNotAvailable}
                                allowEmptyRegistration={courseData.allowEmptyRegistration}
                            />
                        )}
                    </>
                )}

            </div>

            <StepFooter isLoading={isLoadingReal} prevStep={internalPrevStep} nextStep={() => nextStepCheckSuggestions()} />

            <SuggestionsDialog
                courseId={courseId}
                open={isSuggestionsDialogOpen}
                onClose={() => setIsSuggestionsDialogOpen(false)}
                suggestions={((simulateRegistrationData?.suggestions ?? []).filter((s: any) => s.isVisible))}
                sessionType={sessionType}
                isSelected={isSuggestionSelected}
                handleSelect={handleSelectSuggestions}
                handleFoodSelect={handleFoodSelectSuggestions}
                resetSessions={resetSessions}
                nextStep={internalNextStep}
                questionResponse={questionResponse}
                schoolGrade={schoolGrade}
                tmpFixCorsiAdulti={courseData.category.name.toLowerCase().includes('adult')}
                getSessionPriceWithOffset={getSessionPriceWithOffset}
                selectedSessions={selectedSessionsSuggestions}
            />
        </>
    );
};

export default RegistrationStep6;
